import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Sparkles, TrendingUp, Users, CalendarCheck, Wallet, DollarSign,
  ArrowUpRight, Zap, Activity, Home, Flame, ArrowRight, Radio,
  Rocket, Eye, Crown, Image as ImageIcon,
} from "lucide-react";
import { useAuth, apiClient } from "../../lib/auth";
import { buyerLeads, sellerLeads } from "../../lib/dummyData";

// ── DEMO dataset (shown to free / viewing-only clients) ───────────────────
const DEMO_SERIES = {
  "7d": [
    { d: "Mon", leads: 1, appts: 0 }, { d: "Tue", leads: 2, appts: 1 },
    { d: "Wed", leads: 3, appts: 0 }, { d: "Thu", leads: 2, appts: 1 },
    { d: "Fri", leads: 4, appts: 1 }, { d: "Sat", leads: 3, appts: 0 },
    { d: "Sun", leads: 3, appts: 1 },
  ],
  "30d": [
    { d: "Wk1", leads: 3, appts: 1 }, { d: "Wk1", leads: 5, appts: 1 },
    { d: "Wk2", leads: 6, appts: 2 }, { d: "Wk2", leads: 8, appts: 2 },
    { d: "Wk3", leads: 10, appts: 3 }, { d: "Wk3", leads: 13, appts: 3 },
    { d: "Wk4", leads: 16, appts: 4 }, { d: "Now", leads: 18, appts: 4 },
  ],
};
const DEMO_IMG = {
  a: "https://images.unsplash.com/photo-1762811054947-605b20298615?crop=entropy&cs=srgb&fm=jpg&w=400&q=70",
  b: "https://images.unsplash.com/photo-1757359056339-22968344cce6?crop=entropy&cs=srgb&fm=jpg&w=400&q=70",
  c: "https://images.unsplash.com/photo-1757356657991-c3fd6e2e812e?crop=entropy&cs=srgb&fm=jpg&w=400&q=70",
};
const DEMO_RECENT = [
  { name: "Catherine Wells", sub: "Seller · $1.85M listing", status: "Hot", tone: "gold" },
  { name: "Daniel Cho", sub: "Buyer · $2.4M–$3M budget", status: "Qualified", tone: "green" },
  { name: "Robert Ashford", sub: "Seller · $3.2M listing", status: "Appt booked", tone: "blue" },
  { name: "Marcus Holloway", sub: "Buyer · $1.2M–$1.5M budget", status: "New", tone: "slate" },
];
const DEMO_LISTINGS = [
  { title: "742 Evergreen Terrace", sub: "Beverly Hills, CA", badge: "8 leads", img: DEMO_IMG.a },
  { title: "1204 Pacific Heights Blvd", sub: "San Francisco, CA", badge: "6 leads", img: DEMO_IMG.b },
  { title: "88 Marina Vista", sub: "Sausalito, CA", badge: "5 leads", img: DEMO_IMG.c },
];
const DEMO_ACTIVITY = [
  { type: "appt", text: "Appointment booked", sub: "Daniel Cho", time: "12m ago" },
  { type: "lead", text: "New seller lead generated", sub: "Catherine Wells", time: "45m ago" },
  { type: "campaign", text: "Campaign launched", sub: "742 Evergreen Terrace", time: "3h ago" },
  { type: "import", text: "Listing imported", sub: "1204 Pacific Heights Blvd", time: "5h ago" },
];
const ACT_ICON = {
  appt: { icon: CalendarCheck, color: "#10B981" },
  lead: { icon: Flame, color: "#F59E0B" },
  campaign: { icon: Radio, color: "#1D6AFF" },
  import: { icon: Home, color: "#94A3B8" },
  creative: { icon: ImageIcon, color: "#1D6AFF" },
  brand: { icon: Sparkles, color: "#F59E0B" },
};

// ── helpers ───────────────────────────────────────────────────────────────
const fmtMoney = (n) => "$" + Math.round(n).toLocaleString();
const rel = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (Number.isNaN(s)) return "";
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const leadTone = (l) => (l.score >= 9 ? "gold" : l.score >= 7 ? "green" : l.status === "Contacted" ? "blue" : "slate");
const leadStatus = (l) => (l.status === "Contacted" ? "Contacted" : l.score >= 9 ? "Hot" : l.score >= 7 ? "Qualified" : "New");

function useCountUp(target, { duration = 1100, enabled = true } = {}) {
  const [val, setVal] = useState(enabled ? 0 : target);
  const ref = useRef();
  useEffect(() => {
    if (!enabled) { setVal(target); return; }
    let start;
    const step = (t) => {
      if (start === undefined) start = t;
      const p = Math.min((t - start) / duration, 1);
      setVal(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [target, duration, enabled]);
  return val;
}

const containerV = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.07 } } };
const itemV = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } } };

// ── Build the LIVE view from the paying user's engine config + creatives ──
const GOAL_SPLIT = { buyers: [100, 0], sellers: [0, 100], balanced: [50, 50], mostly_buyers: [70, 30], mostly_sellers: [30, 70] };
function engineAllocation(goal, hasListings) {
  let [b, s] = GOAL_SPLIT[goal] || [50, 50];
  let l = 0;
  if (hasListings) { l = 20; b = Math.round(b * 0.8); s = Math.round(s * 0.8); const sum = b + s + l; if (sum !== 100) b += 100 - sum; }
  return [
    { label: "Buyers", pct: b, color: "#1D6AFF" },
    { label: "Sellers", pct: s, color: "#F59E0B" },
    { label: "Listings", pct: l, color: "#10B981" },
  ].filter((x) => x.pct > 0);
}
function buildLiveView(user, creatives) {
  const engine = user?.engine || {};
  const running = !!engine.running;
  const goal = engine.goal || "buyers";
  const budget = engine.budget || 0;

  const startMs = engine.startedAt ? new Date(engine.startedAt).getTime() : null;
  const daysActive = startMs ? Math.min(30, Math.max(1, Math.floor((Date.now() - startMs) / 86400000) + 1)) : 0;
  const spend = running && budget ? Math.min(budget, Math.round((budget / 30) * daysActive)) : 0;

  const buyersOn = goal !== "sellers";
  const sellersOn = goal !== "buyers";

  // Lead feed = the account's leads for the goal types, once the engine is running.
  const feed = [];
  if (running && buyersOn) buyerLeads.forEach((l) => feed.push({ ...l, type: "Buyer", value: l.budget }));
  if (running && sellersOn) sellerLeads.forEach((l) => feed.push({ ...l, type: "Seller", value: l.propertyValue }));
  feed.sort((a, b) => (a.date < b.date ? 1 : -1));

  const leads = feed.length;
  const qualified = feed.filter((l) => l.score >= 7).length;
  const appts = feed.filter((l) => l.score >= 9).length;
  const pipeline = qualified * 2500;

  const activeIds = engine.activeListings || [];
  const hasListings = activeIds.length > 0;
  const splits = running ? engineAllocation(goal, hasListings) : [];

  const recentLeads = feed.slice(0, 4).map((l) => ({
    name: l.name, sub: `${l.type} · ${l.value || "—"}`, status: leadStatus(l), tone: leadTone(l),
  }));

  const sortedCreatives = [...(creatives || [])].sort((a, b) => (activeIds.includes(b.id) ? 1 : 0) - (activeIds.includes(a.id) ? 1 : 0));
  const activeListings = sortedCreatives.slice(0, 4).map((c) => {
    const title = c.property?.address || c.name || "Listing creative";
    let sub = c.property?.city || "";
    if (!sub || sub === title) sub = [c.template, c.size].filter(Boolean).join(" · ") || "Listing ad";
    const promoting = running && activeIds.includes(c.id);
    return { title, sub, badge: promoting ? "Promoting" : "Saved", img: c.thumb, ago: rel(c.created_at) };
  });

  // Activity from real events (engine start + saved creatives), newest first.
  const acts = [];
  if (engine.startedAt) acts.push({ type: "campaign", text: "Lead Engine started", sub: engine.market || "Your market", ts: engine.startedAt });
  (creatives || []).slice(0, 6).forEach((c) =>
    acts.push({ type: "creative", text: "Creative saved", sub: c.property?.address || c.name || "Listing ad", ts: c.created_at })
  );
  acts.sort((a, b) => (new Date(a.ts || 0) < new Date(b.ts || 0) ? 1 : -1));
  const activity = acts.slice(0, 5).map((a) => ({ type: a.type, text: a.text, sub: a.sub, time: rel(a.ts) }));

  // Performance series from real lead dates (live), else flat.
  const byDate = {};
  feed.forEach((l) => {
    byDate[l.date] = byDate[l.date] || { leads: 0, appts: 0 };
    byDate[l.date].leads += 1;
    if (l.score >= 9) byDate[l.date].appts += 1;
  });
  const dates = Object.keys(byDate).sort();
  const pts = dates.map((d) => ({ d: d.slice(5), leads: byDate[d].leads, appts: byDate[d].appts }));
  const series = {
    "7d": pts.slice(-7).length ? pts.slice(-7) : [{ d: "—", leads: 0, appts: 0 }],
    "30d": pts.length ? pts : [{ d: "—", leads: 0, appts: 0 }],
  };

  return {
    demo: false, running, leads, qualified, appts, spend, budget, pipeline,
    splits, recentLeads, activeListings, activity, series,
  };
}

const DEMO_VIEW = {
  demo: true, running: true, leads: 18, qualified: 9, appts: 4, spend: 612, budget: 1000, pipeline: 22000,
  splits: [
    { label: "Buyers", pct: 45, color: "#1D6AFF" },
    { label: "Sellers", pct: 35, color: "#F59E0B" },
    { label: "Listings", pct: 20, color: "#10B981" },
  ],
  recentLeads: DEMO_RECENT, activeListings: DEMO_LISTINGS, activity: DEMO_ACTIVITY, series: DEMO_SERIES,
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [range, setRange] = useState("7d");
  const [creatives, setCreatives] = useState([]);

  const isPaid = ["pro", "elite"].includes(user?.plan);
  const animate = !reduce;

  useEffect(() => {
    if (!isPaid) return;
    let on = true;
    apiClient.get("/creatives/library").then(({ data }) => { if (on) setCreatives(data.items || []); }).catch(() => {});
    return () => { on = false; };
  }, [isPaid]);

  const view = useMemo(() => (isPaid ? buildLiveView(user, creatives) : DEMO_VIEW), [isPaid, user, creatives]);

  // Interactive pointer parallax for the hero's blue glows.
  const mx = useMotionValue(0), my = useMotionValue(0);
  const px = useSpring(mx, { stiffness: 55, damping: 18, mass: 0.6 });
  const py = useSpring(my, { stiffness: 55, damping: 18, mass: 0.6 });
  const px2 = useTransform(px, (v) => v * -1.9);
  const py2 = useTransform(py, (v) => v * -1.9);
  const onHeroMove = (e) => {
    if (!animate) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 50);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 36);
  };
  const onHeroLeave = () => { mx.set(0); my.set(0); };

  const first = (user?.name || "there").split(" ")[0];
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const summary = view.demo
    ? <>Newground generated <b className="text-slate-900">{view.leads} leads</b> and <b className="text-slate-900">{view.appts} hot leads</b> this month.</>
    : view.running
      ? <>Newground generated <b className="text-slate-900">{view.leads} leads</b> and <b className="text-slate-900">{view.appts} hot leads</b> this month.</>
      : <>Your Lead Engine is ready. Launch a campaign and Newground starts finding business for you.</>;

  return (
    <div data-testid="dashboard-page">
      <motion.div variants={containerV} initial={animate ? "hidden" : false} animate="show" className="relative space-y-6 overflow-hidden">
        <div className="pointer-events-none absolute -top-32 -right-20 w-[460px] h-[460px] rounded-full bg-[#1D6AFF]/10 blur-[150px]" />
        <div className="pointer-events-none absolute top-40 -left-28 w-[420px] h-[420px] rounded-full bg-amber-300/10 blur-[150px]" />

        {/* ── HERO ── */}
        <motion.div variants={itemV} className="relative">
          <div onMouseMove={onHeroMove} onMouseLeave={onHeroLeave} className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-[#1D6AFF]/[0.06] via-white to-amber-50/50 p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6 shadow-sm">
            {animate ? (
              <>
                <motion.div aria-hidden className="pointer-events-none absolute inset-0" style={{ x: px, y: py }}>
                  <motion.div className="absolute top-0 left-0 w-80 h-80 rounded-full bg-[#1D6AFF]/20 blur-3xl"
                    animate={{ x: [-60, 80, 10, -60], y: [-70, -10, 40, -70], scale: [1, 1.18, 0.92, 1] }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} />
                  <motion.div className="absolute -bottom-10 left-1/3 w-64 h-64 rounded-full bg-sky-400/14 blur-3xl"
                    animate={{ x: [-30, 50, -10, -30], y: [20, -30, 10, 20], scale: [0.95, 1.2, 1, 0.95] }}
                    transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }} />
                </motion.div>
                <motion.div aria-hidden className="pointer-events-none absolute inset-0" style={{ x: px2, y: py2 }}>
                  <motion.div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-[#2E7DFF]/16 blur-3xl"
                    animate={{ x: [40, -70, 20, 40], y: [-40, 30, -20, -40], scale: [1.1, 0.9, 1.15, 1.1] }}
                    transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} />
                </motion.div>
              </>
            ) : (
              <div className="pointer-events-none absolute -top-24 right-10 w-72 h-72 rounded-full bg-[#1D6AFF]/10 blur-3xl" />
            )}
            <div className="relative min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <StatusChip running={view.running} animate={animate} />
                {view.demo && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1" data-testid="dashboard-demo-badge">
                    <Eye className="w-3 h-3" /> Demo preview
                  </span>
                )}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900" data-testid="dashboard-hero-greeting">
                {greet}, {first} <span className="inline-block">👋</span>
              </h1>
              <p className="mt-3 text-base sm:text-lg text-slate-600" data-testid="dashboard-hero-summary">{summary}</p>
            </div>
            <button
              data-testid="dashboard-promote-listing-cta"
              onClick={() => navigate("/dashboard/creative")}
              className="group relative shrink-0 inline-flex items-center justify-center gap-2 rounded-2xl px-7 py-4 font-bold text-white bg-gradient-to-r from-[#1D6AFF] to-[#2E7DFF] shadow-[0_18px_40px_-12px_rgba(29,106,255,0.55)] hover:shadow-[0_22px_50px_-10px_rgba(29,106,255,0.7)] hover:-translate-y-0.5 transition-all"
            >
              <span className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/25" />
              <Sparkles className="w-5 h-5" /> Promote a Listing
              <ArrowUpRight className="w-4 h-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>
        </motion.div>

        {/* ── DEMO upgrade bar (free / viewing-only) ── */}
        {view.demo && (
          <motion.div variants={itemV}>
            <div data-testid="dashboard-upgrade-bar" className="relative overflow-hidden rounded-2xl border border-[#1D6AFF]/25 bg-gradient-to-r from-[#1D6AFF]/[0.08] to-white p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/25 flex items-center justify-center"><Rocket className="w-5 h-5 text-[#1D6AFF]" /></span>
                <div>
                  <div className="font-display text-base font-bold text-slate-900">This is a live demo of your future dashboard</div>
                  <div className="text-xs text-slate-500">Upgrade to activate your own Lead Engine and see real leads, appointments and listings here.</div>
                </div>
              </div>
              <button data-testid="dashboard-upgrade-btn" onClick={() => navigate("/dashboard/plans")}
                className="shrink-0 inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-bold rounded-xl px-5 py-3 transition-all shadow-[0_14px_30px_-10px_rgba(29,106,255,0.6)]">
                <Crown className="w-4 h-4" /> Upgrade &amp; go live
              </button>
            </div>
          </motion.div>
        )}

        {/* ── KPI CARDS ── */}
        <motion.div variants={itemV} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testId="kpi-card-total-leads" icon={Users} label="Leads" value={view.leads} tone="blue" animate={animate}
            trend={view.demo ? "+32% vs last month" : view.running ? `${view.leads} this month` : "Launch to start"} />
          <KpiCard testId="kpi-card-appointments" icon={Flame} label="High Intent" value={view.appts} tone="gold" animate={animate}
            trend={view.demo ? "+2 this week" : `${view.appts} this month`} />
          <KpiCard testId="kpi-card-monthly-spend" icon={Wallet} label="Spend" value={view.spend} money tone="slate" animate={animate}
            trend={`${fmtMoney(view.budget)} budget`} />
          <KpiCard testId="kpi-card-pipeline-value" icon={DollarSign} label="Pipeline" value={view.pipeline} money tone="gold" animate={animate}
            trend={view.demo ? "+$8,500 this week" : "est. opportunity"} />
        </motion.div>

        {/* ── LEAD ENGINE + PERFORMANCE ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <motion.div variants={itemV} className="lg:col-span-5">
            <LeadEngineCard view={view} animate={animate} onLaunch={() => navigate("/dashboard/leads")} />
          </motion.div>
          <motion.div variants={itemV} className="lg:col-span-7">
            <PerformanceCard range={range} setRange={setRange} view={view} animate={animate} />
          </motion.div>
        </div>

        {/* ── AI RECOMMENDATION ── */}
        <motion.div variants={itemV}>
          <AIRecommendationCard view={view} onView={() => navigate("/dashboard/leads")} animate={animate} />
        </motion.div>

        {/* ── RECENT LEADS / ACTIVE LISTINGS / ACTIVITY ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <motion.div variants={itemV} className="lg:col-span-4"><RecentLeadsCard view={view} onAll={() => navigate("/dashboard/messages")} /></motion.div>
          <motion.div variants={itemV} className="lg:col-span-4"><ActiveListingsCard view={view} onCreate={() => navigate("/dashboard/creative")} /></motion.div>
          <motion.div variants={itemV} className="lg:col-span-4"><ActivityCard view={view} /></motion.div>
        </div>
      </motion.div>
    </div>
  );
}

function StatusChip({ running, animate }) {
  if (running) {
    return (
      <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="dashboard-engine-badge">
        <span className="relative flex h-2 w-2">
          <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 ${animate ? "animate-ping" : ""}`} />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.6)]" />
        </span>
        AI Engine active &amp; scanning
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-3 py-1" data-testid="dashboard-engine-badge">
      <span className="w-2 h-2 rounded-full bg-slate-400" /> Engine paused
    </span>
  );
}

// ───────────────────────────── KPI CARD ──────────────────────────────────
const TONES = {
  blue: { ring: "rgba(29,106,255,0.4)", icon: "#1D6AFF", iconBg: "rgba(29,106,255,0.1)", trend: "text-[#1552C6]", glow: "rgba(29,106,255,0.12)" },
  green: { ring: "rgba(16,185,129,0.4)", icon: "#059669", iconBg: "rgba(16,185,129,0.1)", trend: "text-emerald-700", glow: "rgba(16,185,129,0.12)" },
  gold: { ring: "rgba(245,158,11,0.45)", icon: "#D97706", iconBg: "rgba(245,158,11,0.12)", trend: "text-amber-700", glow: "rgba(245,158,11,0.14)" },
  slate: { ring: "rgba(100,116,139,0.35)", icon: "#475569", iconBg: "rgba(100,116,139,0.1)", trend: "text-slate-600", glow: "rgba(100,116,139,0.1)" },
};
function KpiCard({ testId, icon: Icon, label, value, money, trend, tone = "blue", animate, span }) {
  const t = TONES[tone];
  const n = useCountUp(value, { enabled: animate, duration: 1200 });
  const display = money ? fmtMoney(n) : Math.round(n).toLocaleString();
  return (
    <motion.div whileHover={{ y: -4 }} data-testid={testId} style={{ "--ring": t.ring }}
      className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-[var(--ring)] ${span ? "col-span-2 lg:col-span-1" : ""}`}>
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ boxShadow: `inset 0 0 44px -14px ${t.glow}` }} />
      <div className="relative flex items-center justify-between mb-4">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: t.iconBg }}><Icon className="w-4 h-4" style={{ color: t.icon }} /></span>
        <TrendingUp className="w-4 h-4 text-slate-300 group-hover:text-slate-400 transition-colors" />
      </div>
      <div className="relative">
        <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 tabular-nums" data-testid={`${testId}-value`}>{display}</div>
        <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold ${t.trend}`}><ArrowUpRight className="w-3 h-3" /> {trend}</div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────── LEAD ENGINE ─────────────────────────────────
function LeadEngineCard({ view, animate, onLaunch }) {
  const { spend, budget, running, splits } = view;
  const pct = budget ? Math.min(100, Math.round((spend / budget) * 100)) : 0;
  const width = useCountUp(pct, { enabled: animate, duration: 1400 });
  return (
    <div data-testid="lead-engine-status-card" className="relative h-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col">
      <div className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-emerald-400/10 blur-3xl" />
      <div className="relative flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-[#1D6AFF]/10 flex items-center justify-center"><Zap className="w-4 h-4 text-[#1D6AFF]" /></span>
          <div><div className="text-sm font-bold text-slate-900">Lead Engine</div><div className="text-[11px] text-slate-500">{running ? "Working for you 24/7" : "Ready to launch"}</div></div>
        </div>
        <span data-testid="lead-engine-status-badge" className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${running ? "text-emerald-700 bg-emerald-50 border border-emerald-200" : "text-slate-500 bg-slate-100 border border-slate-200"}`}>
          {running ? (
            <><span className="relative flex h-2 w-2"><span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 ${animate ? "animate-ping" : ""}`} /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.6)]" /></span> Running</>
          ) : (<><span className="w-2 h-2 rounded-full bg-slate-400" /> Paused</>)}
        </span>
      </div>

      {running ? (
        <>
          <div className="relative mb-5" data-testid="lead-engine-budget-progress">
            <div className="flex items-end justify-between mb-2">
              <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{fmtMoney(spend)}<span className="text-sm font-semibold text-slate-400"> / {fmtMoney(budget)}</span></div>
              <div className="text-[11px] font-semibold text-slate-500">{pct}% deployed</div>
            </div>
            <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-[#1D6AFF] to-[#60A5FA] shadow-[0_0_12px_rgba(29,106,255,0.5)] transition-[width]" style={{ width: `${width}%` }} />
            </div>
          </div>
          <div className="relative mt-auto" data-testid="lead-engine-distribution-split">
            {splits.length > 0 && (
              <>
                <div className="flex h-2.5 rounded-full overflow-hidden mb-3">{splits.map((s) => <div key={s.label} style={{ width: `${s.pct}%`, background: s.color }} />)}</div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                  {splits.map((s) => (
                    <div key={s.label} className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="w-2 h-2 rounded-full" style={{ background: s.color }} />{s.label} <span className="font-bold text-slate-900">{s.pct}%</span></div>
                  ))}
                </div>
              </>
            )}
            <div className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-400"><Activity className="w-3.5 h-3.5 text-emerald-500" /> Real-time budget optimization active</div>
          </div>
        </>
      ) : (
        <div className="relative mt-auto flex flex-col items-start gap-3 py-2" data-testid="lead-engine-empty">
          <p className="text-sm text-slate-500 leading-relaxed">Your engine is set up but not running yet. Launch a buyer or seller campaign and Newground starts generating opportunities automatically.</p>
          <button onClick={onLaunch} className="inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors">
            <Rocket className="w-4 h-4" /> Launch your Lead Engine
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── PERFORMANCE ─────────────────────────────────
function PerformanceCard({ range, setRange, view, animate }) {
  const data = view.series[range];
  return (
    <div data-testid="performance-chart-card" className="relative h-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div><div className="text-sm font-bold text-slate-900">Performance</div><div className="text-[11px] text-slate-500 mt-0.5">{view.running ? "Leads & appointments over time" : "Launch a campaign to see your trend"}</div></div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          {["7d", "30d"].map((r) => (
            <button key={r} data-testid={`chart-timeframe-${r}-button`} onClick={() => setRange(r)}
              className={`text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all ${range === r ? "bg-[#1D6AFF] text-white shadow-[0_6px_16px_-6px_rgba(29,106,255,0.6)]" : "text-slate-500 hover:text-slate-900"}`}>
              {r === "7d" ? "7 Days" : "30 Days"}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-6 mb-2">
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#1D6AFF]" /><span className="text-xs text-slate-600">Leads <span className="font-bold text-slate-900">{view.leads}</span></span></div>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /><span className="text-xs text-slate-600">High Intent <span className="font-bold text-slate-900">{view.appts}</span></span></div>
      </div>
      <div className="flex-1 min-h-[220px]" data-testid="performance-recharts-container">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 6, left: -24, bottom: 0 }}>
            <defs>
              <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1D6AFF" stopOpacity={0.35} /><stop offset="100%" stopColor="#1D6AFF" stopOpacity={0} /></linearGradient>
              <linearGradient id="gAppts" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.3} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
            <XAxis dataKey="d" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={34} />
            <Tooltip content={<GlassTooltip />} cursor={{ stroke: "rgba(15,23,42,0.12)" }} />
            <Area type="monotone" dataKey="leads" stroke="#1D6AFF" strokeWidth={2.5} fill="url(#gLeads)" isAnimationActive={animate} animationDuration={900} dot={false} />
            <Area type="monotone" dataKey="appts" stroke="#F59E0B" strokeWidth={2.5} fill="url(#gAppts)" isAnimationActive={animate} animationDuration={900} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
function GlassTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-lg">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-xs text-slate-700"><span className="w-2 h-2 rounded-full" style={{ background: p.color }} /><span className="capitalize">{p.dataKey === "appts" ? "High Intent" : "Leads"}</span><span className="font-bold text-slate-900 ml-auto">{p.value}</span></div>
      ))}
    </div>
  );
}

// ──────────────────────── AI RECOMMENDATION ──────────────────────────────
function AIRecommendationCard({ view, onView, animate }) {
  const text = view.demo
    ? "Seller leads are converting better this month, so Newground automatically shifted more of your budget toward them. No action needed."
    : view.running
      ? "Your campaigns are live and Newground is optimizing budget toward your best-converting audience in real time."
      : "Launch a campaign and Newground's AI will start optimizing your budget automatically toward the leads most likely to convert.";
  return (
    <div data-testid="ai-recommendation-card" className="relative overflow-hidden rounded-2xl border border-[#1D6AFF]/20 bg-gradient-to-r from-[#1D6AFF]/[0.07] to-white p-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-5">
      <div className="pointer-events-none absolute -top-16 left-1/3 w-64 h-64 rounded-full bg-[#1D6AFF]/10 blur-3xl" />
      <div className={`relative shrink-0 w-12 h-12 rounded-2xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/25 flex items-center justify-center ${animate ? "animate-pulse" : ""} shadow-[0_0_22px_rgba(29,106,255,0.25)]`}><Sparkles className="w-6 h-6 text-[#1D6AFF]" /></div>
      <div className="relative min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#1552C6]">AI Recommendation</span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{view.running ? "Auto-optimized" : "Ready"}</span>
        </div>
        <p className="text-sm sm:text-base text-slate-700 leading-relaxed" data-testid="ai-recommendation-text">{text}</p>
      </div>
      <button data-testid="ai-recommendation-action-button" onClick={onView} className="relative shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 transition-colors">View details <ArrowRight className="w-4 h-4" /></button>
    </div>
  );
}

// ─────────────────────────── RECENT LEADS ────────────────────────────────
const STATUS_TONE = {
  gold: "text-amber-700 bg-amber-50 border-amber-200",
  green: "text-emerald-700 bg-emerald-50 border-emerald-200",
  blue: "text-[#1552C6] bg-blue-50 border-blue-200",
  slate: "text-slate-600 bg-slate-100 border-slate-200",
};
function RecentLeadsCard({ view, onAll }) {
  return (
    <div data-testid="recent-leads-card" className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <CardHead icon={Flame} title="Recent Leads" onAll={view.recentLeads.length ? onAll : null} />
      {view.recentLeads.length ? (
        <div className="space-y-2.5 mt-4">
          {view.recentLeads.map((l, i) => (
            <div key={i} data-testid="recent-lead-item" className="group flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 hover:bg-slate-100 px-3 py-2.5 transition-colors">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1D6AFF] to-[#0B3FA3] flex items-center justify-center text-xs font-bold text-white shrink-0">{l.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}</div>
              <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-slate-900 truncate">{l.name}</div><div className="text-[11px] text-slate-500 truncate">{l.sub}</div></div>
              <span className={`text-[10px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 shrink-0 ${STATUS_TONE[l.tone]}`}>{l.status}</span>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={Flame} text="No leads yet. Launch your Lead Engine and new leads will land here automatically." />
      )}
    </div>
  );
}

// ────────────────────────── ACTIVE LISTINGS ──────────────────────────────
function ActiveListingsCard({ view, onCreate }) {
  return (
    <div data-testid="active-listings-card" className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <CardHead icon={Home} title="Active Listings" onAll={view.activeListings.length ? onCreate : null} />
      {view.activeListings.length ? (
        <div className="space-y-3 mt-4">
          {view.activeListings.map((p, i) => (
            <div key={i} data-testid="active-listing-item" className="group flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 hover:bg-slate-100 p-2 pr-3 transition-colors">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-200 shrink-0">{p.img ? <img src={p.img} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center"><Home className="w-5 h-5 text-slate-400" /></div>}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{p.title}</div>
                <div className="text-[11px] text-slate-500 truncate">{p.sub}</div>
                <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {p.badge}{p.ago ? ` · ${p.ago}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={Home} text="No listings promoted yet." action="Create a listing ad" onAction={onCreate} />
      )}
    </div>
  );
}

// ───────────────────────────── ACTIVITY ──────────────────────────────────
function ActivityCard({ view }) {
  return (
    <div data-testid="recent-activity-card" className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <CardHead icon={Activity} title="Recent Activity" />
      {view.activity.length ? (
        <div className="relative mt-4 space-y-4">
          <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200" />
          {view.activity.map((a, i) => {
            const cfg = ACT_ICON[a.type] || ACT_ICON.import;
            return (
              <div key={i} data-testid="recent-activity-item" className="relative flex items-start gap-3">
                <span className="relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 border border-slate-200" style={{ background: `${cfg.color}14` }}><cfg.icon className="w-3.5 h-3.5" style={{ color: cfg.color }} /></span>
                <div className="min-w-0 flex-1 pt-0.5"><div className="text-sm text-slate-800 leading-tight">{a.text}</div><div className="text-[11px] text-slate-500 mt-0.5">{a.sub}{a.time ? ` · ${a.time}` : ""}</div></div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState icon={Activity} text="Your activity will appear here as Newground works — campaigns, new leads and booked appointments." />
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, text, action, onAction }) {
  return (
    <div className="mt-4 flex flex-col items-center text-center py-6">
      <span className="w-11 h-11 rounded-2xl bg-slate-100 flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-slate-400" /></span>
      <p className="text-xs text-slate-500 leading-relaxed max-w-[220px]">{text}</p>
      {action && <button onClick={onAction} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1D6AFF] hover:text-[#1552C6]">{action} <ArrowRight className="w-3 h-3" /></button>}
    </div>
  );
}

function CardHead({ icon: Icon, title, onAll }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5"><span className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center"><Icon className="w-4 h-4 text-slate-500" /></span><h3 className="text-sm font-bold text-slate-900">{title}</h3></div>
      {onAll && <button onClick={onAll} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 transition-colors">View all <ArrowRight className="w-3 h-3" /></button>}
    </div>
  );
}
